import { useState, useEffect } from "react";
import { api, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";

/* ----------------- BRAND ----------------- */
export function BrandTab() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/public/brand").then((r) => setB(r.data)); }, []);
  if (!b) return null;
  const save = async () => {
    await api.post("/admin/brand", b);
    // apply live without reload
    const root = document.documentElement;
    if (b.primary_hex) root.style.setProperty("--brand-gold", b.primary_hex);
    if (b.primary_hi_hex) root.style.setProperty("--brand-gold-hi", b.primary_hi_hex);
    if (b.ink_hex) root.style.setProperty("--brand-cream", b.ink_hex);
    if (b.bg_hex) root.style.setProperty("--brand-void", b.bg_hex);
    if (b.surface_hex) root.style.setProperty("--brand-surface", b.surface_hex);
    if (b.border_hex) root.style.setProperty("--brand-border", b.border_hex);
    toast.success("Brand saved · applied live");
  };
  // Auto-derive harmonious ombre when primary changes
  const lighten = (hex, amt) => {
    const n = parseInt(hex.replace("#",""), 16);
    const r = Math.min(255, ((n >> 16) & 255) + amt);
    const g = Math.min(255, ((n >> 8) & 255) + amt);
    const bl = Math.min(255, (n & 255) + amt);
    return "#" + [r,g,bl].map(x => x.toString(16).padStart(2,"0")).join("");
  };
  const setPrimary = (hex) => setB({ ...b, primary_hex: hex, primary_hi_hex: lighten(hex, 18) });
  const colorField = (k, label, onChange) => (
    <div>
      <label className="overline">{label}</label>
      <div className="flex items-center gap-3 mt-2">
        <input type="color" value={b[k] || "#000000"} onChange={(e) => (onChange ? onChange(e.target.value) : setB({ ...b, [k]: e.target.value }))} style={{width:56,height:40,padding:0,border:"1px solid #332D21"}}/>
        <input value={b[k] || ""} onChange={(e) => (onChange ? onChange(e.target.value) : setB({ ...b, [k]: e.target.value }))} className="font-mono" style={{maxWidth:140}}/>
      </div>
    </div>
  );
  const fontOptions = ["Cormorant Garamond","Outfit","Inter","Playfair Display","Lora","DM Serif Display","Manrope","JetBrains Mono","Space Mono","Crimson Pro"];
  const fontField = (k, label) => (
    <div>
      <label className="overline">{label}</label>
      <select value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })}>
        {fontOptions.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
    </div>
  );
  return (
    <div className="space-y-5 max-w-3xl">
      <div className="overline">// BRAND · APPLIED SITE-WIDE</div>
      <div><label className="overline">// SITE NAME</label><input value={b.site_name || ""} onChange={(e) => setB({ ...b, site_name: e.target.value })}/></div>
      <div><label className="overline">// TAGLINE</label><input value={b.tagline || ""} onChange={(e) => setB({ ...b, tagline: e.target.value })}/></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {colorField("primary_hex", "// PRIMARY (auto-derives hover)", setPrimary)}
        {colorField("primary_hi_hex", "// PRIMARY HOVER")}
        {colorField("ink_hex", "// TEXT")}
        {colorField("bg_hex", "// BACKGROUND")}
        {colorField("surface_hex", "// SURFACE")}
        {colorField("border_hex", "// BORDER")}
      </div>
      <div className="panel p-5">
        <div className="overline mb-3">// LIVE PREVIEW</div>
        <div style={{background:b.bg_hex,color:b.ink_hex,padding:24,borderRadius:0,border:`1px solid ${b.border_hex}`}}>
          <div style={{color:b.primary_hex,fontFamily:"JetBrains Mono",letterSpacing:"0.3em",fontSize:11,marginBottom:8}}>// {b.site_name?.toUpperCase()}</div>
          <div style={{fontFamily:b.display_font || "Cormorant Garamond",fontSize:32,lineHeight:1.1}}>The increase obeys the steward.</div>
          <button style={{background:b.primary_hex,color:b.bg_hex,padding:"12px 24px",marginTop:16,letterSpacing:"0.12em",textTransform:"uppercase",fontSize:12,fontFamily:b.body_font || "Outfit",border:0}}>Sample button</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        {fontField("display_font", "// DISPLAY FONT")}
        {fontField("body_font", "// BODY FONT")}
        {fontField("mono_font", "// MONO FONT")}
      </div>
      <div><label className="overline">// LOGO URL (optional)</label><input value={b.logo_url || ""} onChange={(e) => setB({ ...b, logo_url: e.target.value })}/></div>
      <button data-testid="brand-save" onClick={save} className="btn-gold">Save brand & apply live</button>
    </div>
  );
}

/* ----------------- PRICING ----------------- */
export function PricingTab() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/pricing").then((r) => setP(r.data)); }, []);
  if (!p) return null;
  const save = async () => { await api.post("/admin/pricing", p); toast.success("Pricing saved (applies on next /pricing visit)"); };
  const dollarInput = (k, label) => (
    <div>
      <label className="overline">{label}</label>
      <div className="flex items-center gap-2">
        <span className="text-gold text-xl">$</span>
        <input type="number" step="0.01" value={(p[k] || 0) / 100} onChange={(e) => setP({ ...p, [k]: Math.round(Number(e.target.value) * 100) })}/>
      </div>
    </div>
  );
  return (
    <div className="space-y-5 max-w-2xl">
      <div className="overline">// PRICING & OFFER VISIBILITY</div>
      <p className="text-textMuted text-sm">Enter dollar amounts. We auto-convert to cents for Stripe. <strong className="text-cream">Important:</strong> changes here only apply to NEW subscriptions. Existing members keep their original Stripe price until they cancel/re-subscribe. To update existing members in bulk, use the Stripe Dashboard.</p>
      {dollarInput("full_monthly_cents", "// FOUNDER MONTHLY PRICE")}
      {dollarInput("full_after_promo_monthly_cents", "// POST-PROMO MONTHLY PRICE")}
      {dollarInput("full_annual_cents", "// FOUNDER ANNUAL PRICE")}
      {dollarInput("full_after_promo_annual_cents", "// POST-PROMO ANNUAL PRICE")}
      {dollarInput("foundational_monthly_cents", "// FOUNDATIONAL (DOWNGRADE) PRICE")}
      <div><label className="overline">// PROMO WINDOW (days)</label><input type="number" value={p.promo_days} onChange={(e) => setP({ ...p, promo_days: Number(e.target.value) })}/></div>
      <div><label className="overline">// MEMBERSHIP CAP</label><input type="number" value={p.cap} onChange={(e) => setP({ ...p, cap: Number(e.target.value) })}/></div>
      <label className="flex items-center gap-2 text-sm text-textMuted"><input type="checkbox" checked={!!p.show_foundational_publicly} onChange={(e) => setP({ ...p, show_foundational_publicly: e.target.checked })} style={{width:16,height:16}}/>Show $11 Foundational publicly on /pricing</label>
      <button data-testid="pricing-save" onClick={save} className="btn-gold">Save pricing</button>
      <CompCoupon />
      <div className="mt-8 pt-6 border-t border-borderGold" data-testid="codex-download">
        <div className="overline mb-2">// BONUS · THE SOVEREIGN CONVERSION CODEX</div>
        <p className="text-textMuted text-sm mb-4">An honest PDF of every conversion principle applied to your sales flow — each reframed through Kingdom ethos (ruling over Mammon &amp; Chronos, not serving them). Yours to keep, teach, or hand to affiliates.</p>
        <a data-testid="codex-link" href={`${BACKEND_URL}/api/admin/downloads/conversion-codex`} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex">Download the Codex (PDF)</a>
      </div>
      <div className="mt-6" data-testid="theology-download">
        <div className="overline mb-2">// DOCTRINE · THE THEOLOGY OF INCREASE &amp; TIME</div>
        <p className="text-textMuted text-sm mb-4">The biblical brief behind the Marks of the Steward: ruling over increase (not worshipping it), and casting out Chronos to operate in Kairos — plus the design covenant that keeps the gamification from ever discipling members into Mammon.</p>
        <a data-testid="theology-link" href={`${BACKEND_URL}/api/admin/downloads/theology-of-increase`} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex">Download the Doctrine (PDF)</a>
      </div>
    </div>
  );
}

function CompCoupon() {
  const [code, setCode] = useState("FOUNDERCOMP");
  const [percent, setPercent] = useState(100);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/test-coupon", { code, percent_off: percent });
      setResult(data);
      toast.success(data.existing ? "Code already existed — ready to use" : "Comp code created in Stripe");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create coupon"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 pt-6 border-t border-borderGold" data-testid="comp-coupon">
      <div className="overline mb-2">// TEST / COMP COUPON</div>
      <p className="text-textMuted text-sm mb-4">Generate a Stripe promo code (default <strong className="text-cream">100% off</strong>) so you — or a comped guest — can complete checkout and test the full member + email flow without paying yourself. Customers enter it in the "Add promotion code" box on the Stripe checkout page.</p>
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2"><label className="overline">// CODE</label><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="FOUNDERCOMP"/></div>
        <div><label className="overline">// % OFF</label><input type="number" min={1} max={100} value={percent} onChange={(e) => setPercent(Number(e.target.value))}/></div>
      </div>
      <button data-testid="generate-coupon" onClick={generate} disabled={busy} className="btn-gold mt-4 disabled:opacity-60">{busy ? "Creating…" : "Generate Stripe code"}</button>
      {result && (
        <div className="mt-4 panel p-4">
          <div className="text-cream">Use this code at checkout: <span className="font-display text-2xl text-gold">{result.code}</span> <span className="text-textDim text-sm">({result.percent_off}% off)</span></div>
          <button onClick={() => { navigator.clipboard.writeText(result.code); toast.success("Copied"); }} className="btn-ghost text-xs mt-3">Copy code</button>
        </div>
      )}
    </div>
  );
}

/* ----------------- NOTIF PREFS ----------------- */
export function NotifPrefsTab() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/me/notif-prefs").then((r) => setP(r.data.prefs)); }, []);
  if (!p) return null;
  const save = async () => { await api.patch("/me/notif-prefs", p); toast.success("Saved"); };
  const tog = (k, label, desc) => (
    <label className="block panel p-4 cursor-pointer">
      <div className="flex items-center justify-between gap-4">
        <div><div className="text-cream">{label}</div><div className="text-xs font-mono text-textDim mt-1">{desc}</div></div>
        <input type="checkbox" checked={!!p[k]} onChange={(e) => setP({ ...p, [k]: e.target.checked })} style={{width:20,height:20}}/>
      </div>
    </label>
  );
  return (
    <div className="space-y-3 max-w-2xl">
      <div className="overline">// EMAIL ME WHEN…</div>
      {tog("admin_on_signup", "A new person signs up", "Catches them at the warmest moment. Send a personal welcome.")}
      {tog("admin_on_purchase", "Someone joins (paid)", "Money just hit. Celebrate it and send them a personal note.")}
      {tog("admin_on_cancel", "Someone cancels", "Reach out personally — sometimes it's just a season.")}
      {tog("admin_on_testimonial", "A testimonial is submitted", "Approve and feature within 24 hours.")}
      {tog("admin_on_payment_failed", "A payment fails", "You may want to DM them while Stripe auto-retries.")}
      {tog("admin_on_payment_succeeded", "A renewal succeeds", "See when a customer successfully renews their membership.")}
      {tog("admin_on_subscription_paused", "A subscription is paused", "Monitor members who pause so you can follow up.")}
      {tog("admin_on_subscription_resumed", "A subscription is resumed", "Know when a member returns to active status.")}
      {tog("admin_on_lead", "A blog lead opts in", "Useful when running ads. Off by default.")}
      {tog("admin_on_post", "A community post is made", "Useful at launch, off as it grows.")}
      <div className="mt-6 p-4 rounded border border-gold/20 bg-gold/5 text-sm text-cream">
        Automatic re-engagement is active for inactive members. The platform sends up to three members-only re-entry emails at 14, 21, and 28 days of inactivity.
      </div>
      <div className="overline mt-6">// DIGEST FREQUENCY</div>
      <select value={p.admin_digest_frequency} onChange={(e) => setP({ ...p, admin_digest_frequency: e.target.value })}>
        <option value="instant">Instant (every event)</option>
        <option value="hourly">Hourly rollup</option>
        <option value="daily">Daily digest (recommended)</option>
        <option value="off">Off — no emails to me</option>
      </select>
      <button data-testid="notif-save" onClick={save} className="btn-gold">Save preferences</button>
    </div>
  );
}
